import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { MaybeAsync } from '@angular/router';
import { ButtonComponent } from '@shared/button/button.component';
import { Helpers } from '@shared/helpers';
import { PreloaderModule } from '@shared/preloader/preloader.module';
import { zbpIconNames } from '@shared/zbp-icon/zbp-icon-names';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'zbp-audio-player',
  templateUrl: './audio-player.component.html',
  styleUrls: ['./audio-player.component.scss'],
  imports: [CommonModule, ButtonComponent, PreloaderModule]
})
export class AudioPlayerComponent implements OnInit, OnChanges {
  @Input() playerTitle?: string;
  @Input() assetName?: string;
  /** see {@link _audioFile$} */
  @Input() audioFile: MaybeAsync<string>;
  @Input() autoplay: boolean = false;

  @ViewChild('audio') audioElement: ElementRef<HTMLAudioElement>;

  protected readonly zbpIconNames = zbpIconNames;
  private readonly AUDIO_ERROR_MESSAGE = 'The audio file could not be loaded. Please contact Customer Support.';

  /** this is the actual value referenced by the template */
  _audioFile$: Observable<string> = of(undefined);

  time: string = this.formatSecondsToMinutes(0);
  duration: string = this.formatSecondsToMinutes(0);
  playButtonText = 'Play';
  icon = zbpIconNames.audioPlayIcon;
  announcement: string = '';
  errorMessage: string | null = null;

  constructor() { }

  ngOnInit(): void {
    this.setTimeAndDuration();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.audioFile) {
      this._audioFile$ = Helpers.maybeAsyncToObservable(changes.audioFile.currentValue).pipe(
        catchError((err) => {
          console.error('Audio Player Error', err);
          this.setErrorMessage(this.AUDIO_ERROR_MESSAGE);
          return null;
        })
      );
      this.setErrorMessage(null);
      this.setTimeAndDuration();
      this.pauseStarted();
    }
  }

  setTimeAndDuration(): void {
    this.duration = this.formatSecondsToMinutes(0);
    this.time = this.formatSecondsToMinutes(0);
  }

  setTime(event) {
    this.time = this.formatSecondsToMinutes(Math.floor(event.target.currentTime));
  }

  setDuration(event) {
    this.duration = this.formatSecondsToMinutes(Math.floor(event.target.duration));
  }

  private formatSecondsToMinutes(totalSeconds) {
    if (isNaN(totalSeconds)) { return '00:00'; }
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    // Pad with a leading zero if the number is less than 10
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(seconds).padStart(2, '0');

    return `${formattedMinutes}:${formattedSeconds}`;
  }

  skipForward(seconds: number) {
    const audio = this.audioElement?.nativeElement;
    if (!audio) {
      this.announcement = 'Audio not ready';
      return;
    }
    let newTime = audio.currentTime + seconds;
    const dur = audio.duration;
    if (!isNaN(dur) && newTime > dur) {
      newTime = dur;
    }
    audio.currentTime = newTime;
    this.announcement = `Fast forward ${seconds} seconds. Now at ${this.formatSecondsToMinutes(Math.floor(newTime))}`;
  }

  skipBackwards(seconds: number) {
    const audio = this.audioElement?.nativeElement;
    if (!audio) {
      this.announcement = 'Audio not ready';
      return;
    }
    let newTime = audio.currentTime - seconds;
    if (newTime < 0) {
      newTime = 0;
    }
    audio.currentTime = newTime;
    this.announcement = `Rewind ${seconds} seconds. Now at ${this.formatSecondsToMinutes(Math.floor(newTime))}`;
  }

  playOrPause() {
    const audio = this.audioElement?.nativeElement;
    if (!audio) return;

    if (audio.paused) {
      audio.play().catch((e: DOMException) => {
        console.error('Audio Player Error', e);
        this.setErrorMessage(this.AUDIO_ERROR_MESSAGE);
      });
    } else {
      audio.pause();
    }
  }

  playStarted() {
    this.playButtonText = 'Pause';
    this.icon = zbpIconNames.audioPauseIcon;
  }

  pauseStarted() {
    this.playButtonText = 'Play';
    this.icon = zbpIconNames.audioPlayIcon;
  }

  onAudioError(event: Event): void {
    console.error('Audio Player Error', event);
    this.setErrorMessage(this.AUDIO_ERROR_MESSAGE);
  }

  setErrorMessage(message: string): void {
    this.errorMessage = message;
    if (this.autoplay) {
      this.announcement = message;
    }
  }
}
